// src/lib/format.js — formatação BRL + datas
// Portado verbatim do monolítico (~linha 22572).

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function fmt(v) {
  return BRL.format(Number(v) || 0);
}

export function fmtNum(v, dec = 0) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(Number(v) || 0);
}

/**
 * Formata data PT-BR. Aceita 'YYYY-MM-DD' ou ISO completo.
 * Usa noon UTC pra evitar bug de timezone (data shift -1 dia).
 */
export function fmtDate(d) {
  if (!d) return '—';
  try {
    const str = typeof d === 'string' && d.length === 10 ? d + 'T12:00:00' : d;
    return new Date(str).toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}

export function fmtDateTime(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('pt-BR');
  } catch {
    return '—';
  }
}

export function fmtPct(v) {
  return `${Number(v) || 0}%`;
}

/**
 * Parser de valor monetário tolerante a formato brasileiro.
 * Aceita "1.126,67" (pt-BR), "1126,67", "1126.67" e "R$ 1.126,67".
 * Regra: se houver vírgula, ela é o separador decimal e os pontos são milhar;
 * se não houver vírgula, o ponto é tratado como decimal. Retorna Number (0 se inválido).
 */
export function parseMoeda(raw) {
  if (raw === null || raw === undefined) return 0;
  let s = String(raw).trim().replace(/[R$\s]/g, '');
  if (!s) return 0;
  if (s.indexOf(',') >= 0) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Tempo relativo simples ("há 5 min", "ontem", "há 3 dias").
 * Inspirado em _cliRelTime do monolítico.
 */
export function relTime(ts) {
  if (!ts) return '—';
  try {
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return 'agora';
    if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
    if (diff < 172800) return 'ontem';
    return `há ${Math.floor(diff / 86400)} dias`;
  } catch {
    return '—';
  }
}

/**
 * Nomes vêm do banco em CAIXA ALTA ("CASA MAX E LILI ALPHAVILLE 4", "MARCELL OLIVEIRA")
 * porque foram digitados assim no cadastro. Em breadcrumb e título, caixa alta grita —
 * e o shadcn não usa versalete em navegação.
 *
 * Só age quando o texto é *majoritariamente* maiúsculo: se a pessoa escreveu com caixa
 * mista, respeitamos o que ela digitou. Preserva siglas (LTDA, BSB, SP) e mantém as
 * preposições minúsculas ("Casa Max e Lili", não "Casa Max E Lili").
 */
const _SIGLAS = new Set([
  'LTDA', 'ME', 'EPP', 'MEI', 'EIRELI', 'SA', 'S/A', 'CIA', 'LTD', 'INC',
  'CNPJ', 'CPF', 'RG', 'NR', 'RDO', 'BM', 'NF', 'NFE', 'NFSE', 'CEI', 'ART',
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);
const _MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'na', 'no', 'nas', 'nos', 'a', 'o', 'ao', 'aos', 'com', 'para', 'por', 'sob', 'sobre']);

export function nomeProprio(txt) {
  const s = String(txt || '').trim();
  if (!s) return s;
  const letras = s.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (letras.length < 3) return s;
  const maiusculas = (letras.match(/[A-ZÀ-Þ]/g) || []).length;
  if (maiusculas / letras.length < 0.8) return s;      // caixa mista: não mexe

  return s.toLocaleLowerCase('pt-BR')
    .split(/(\s+|-|\/)/)
    .map((tk, i) => {
      if (!tk || /^(\s+|-|\/)$/.test(tk)) return tk;
      const limpo = tk.replace(/[.,;:]/g, '');
      const up = limpo.toLocaleUpperCase('pt-BR');
      if (_SIGLAS.has(up)) return tk.toLocaleUpperCase('pt-BR');
      // sigla sem vogal (BSB, CTG) ou número romano curto
      if (limpo.length <= 4 && !/[aeiouáéíóúâêôàãõ]/i.test(limpo) && /[a-z]/i.test(limpo)) return tk.toLocaleUpperCase('pt-BR');
      if (i > 0 && _MINUSCULAS.has(limpo)) return tk;
      return tk.charAt(0).toLocaleUpperCase('pt-BR') + tk.slice(1);
    })
    .join('');
}
